import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  getOwedFeesByDate,
  getOwedFeesByDateCsv,
  sendInvoiceRequest,
  sendProformaRequest,
  updateOwedFees,
  updateCollectorFeePaymentStatus,
} from "../../Utils/RestUtils";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  CardContent,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Close } from "@mui/icons-material";
import { useNotify } from "react-admin";

function ListOwedFees() {
  const [owedFees, setOwedFees] = useState();
  const [feesDetails, setFeesDetails] = useState(null);
  const [filteringDate, setFilteringDate] = useState(new Date());
  const [showDialog, setShowDialog] = useState(false);
  const [proformaButtonDisabled, setProformaButtonDisabled] = useState(false);
  const [owedFeesModified, setOwedFeesModified] = useState(false);
  const [feeToRemove, setFeeToRemove] = useState(null);
  const [removingFee, setRemovingFee] = useState(false);
  const notify = useNotify();

  function filterSelectedDate(value) {
    let filterDate = filteringDate;
    if (value) {
      filterDate = value;
    }

    return filterDate.getMonth() + 1 + "-" + filterDate.getFullYear();
  }

  async function loadOwedFeesFromApi() {
    const dateToFilter = filterSelectedDate();
    setOwedFees(await getOwedFeesByDate(dateToFilter));
  }

  useEffect(() => {
    loadOwedFeesFromApi();
  }, []);

  async function handleDateChange(selectedDate) {
    if (!selectedDate.isValid()) return;
    setFilteringDate(selectedDate.toDate());

    const dateToFilter = filterSelectedDate(selectedDate.toDate());
    setOwedFees(await getOwedFeesByDate(dateToFilter));
  }

  function handleExportClick(event) {
    event.preventDefault();

    const dateToFilter = filterSelectedDate(filteringDate);
    getOwedFeesByDateCsv(dateToFilter);
  }

  async function refreshOwedFeesList(key) {
    const dateToFilter = filterSelectedDate(filteringDate);
    await updateOwedFees(owedFees.data[key].collection_agent_id, dateToFilter);
    setOwedFees(await getOwedFeesByDate(dateToFilter));
  }

  const handleClose = () => {
    setShowDialog(false);
    if (owedFeesModified) {
      loadOwedFeesFromApi();
      setOwedFeesModified(false);
    }
  };

  function requestRemoveLead(key, lead) {
    setFeeToRemove({ key, lead });
  }

  function cancelRemoveLead() {
    if (removingFee) return;
    setFeeToRemove(null);
  }

  async function confirmRemoveLead() {
    if (feeToRemove === null) return;

    const { key, lead } = feeToRemove;
    setRemovingFee(true);
    const response = await updateCollectorFeePaymentStatus(
      lead.collector_fee_id,
      2
    );
    setRemovingFee(false);
    setFeeToRemove(null);

    if (response.status === 200) {
      const updatedLeads = { ...feesDetails.collection_agent_leads };
      delete updatedLeads[key];
      setFeesDetails({
        ...feesDetails,
        collection_agent_leads: updatedLeads,
      });
      setOwedFeesModified(true);
      notify("Fee removed successfully", { type: "success" });
    } else {
      notify("Failed to remove fee", { type: "error" });
    }
  }

  async function sendInvoice(type, collectionAgentId) {
    let response;
    if (type === "proforma") {
      setProformaButtonDisabled(true);
      response = await sendProformaRequest(
        collectionAgentId,
        filterSelectedDate()
      );
      setProformaButtonDisabled(false);
    } else {
      response = await sendInvoiceRequest(collectionAgentId, filteringDate);
    }

    if (response.status === 200) {
      notify("Invoice has been sent", { type: "success" });
    } else {
      notify("Invoice failed to send", { type: "error" });
    }
  }

  function hasInvoice(owedFee) {
    const leads = owedFee.collection_agent_leads ?? {};
    const firstLeadKey = Object.keys(leads)[0];

    return Boolean(leads[firstLeadKey]?.freshbooks_invoice_id);
  }

  const OwedFeesList = () => {
    if (owedFees.count == 0 || owedFees.data == undefined) {
      return (
        <Alert icon={false} severity="error">
          No invoices were found for this month.
        </Alert>
      );
    }

    return (
      <div>
        <Box style={{ display: "flex", marginTop: "10px" }}>
          <Card style={{ flex: "1 1 auto" }}>
            <Table sx={{ padding: 2 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Collection Agent ID
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Phone Number
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Amount Owed
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(owedFees.data).map((key) => (
                  <TableRow key={key}>
                    <TableCell>
                      {owedFees.data[key].collection_agent_id}
                    </TableCell>
                    <TableCell>
                      {owedFees.data[key].collection_agent_company_name}
                    </TableCell>
                    <TableCell>
                      {owedFees.data[key].collection_agent_contact_tel_num}
                    </TableCell>
                    <TableCell>
                      $
                      {parseFloat(owedFees.data[key].total_to_invoice).toFixed(
                        2
                      )}
                    </TableCell>
                    <TableCell>{owedFees.data[key].payment_status}</TableCell>
                    <TableCell>
                      <Button
                        sx={{ marginBottom: "5px", width: "100%" }}
                        onClick={() => {
                          setShowDialog(true);
                          setFeesDetails(owedFees.data[key]);
                        }}
                      >
                        View details
                      </Button>
                      <br />
                      {owedFees.data[key].payment_status.toLowerCase() !==
                        "paid" && (
                        <Button
                          sx={{ marginBottom: "5px", width: "100%" }}
                          onClick={() => {
                            refreshOwedFeesList(key);
                          }}
                        >
                          Mark as paid
                        </Button>
                      )}
                      <br />
                      {/* This nasty logic checks if owed fee entity has assigned freshbooks_invoice_id*/}
                      {owedFees.data[key].collection_agent_leads[
                        Object.keys(
                          owedFees.data[key].collection_agent_leads
                        )[0]
                      ]?.freshbooks_invoice_id === "" && (
                        <Button
                          disabled={proformaButtonDisabled}
                          sx={{ marginBottom: "5px", width: "100%" }}
                          onClick={() => {
                            sendInvoice(
                              "proforma",
                              owedFees.data[key].collection_agent_id
                            );
                          }}
                        >
                          Send proforma
                        </Button>
                      )}
                      <Button
                        sx={{ marginBottom: "5px", width: "100%" }}
                        onClick={async () => {
                          await sendInvoice(
                            "full",
                            owedFees.data[key].collection_agent_id
                          );
                          await loadOwedFeesFromApi();
                        }}
                      >
                        {hasInvoice(owedFees.data[key])
                          ? "Resend invoice"
                          : "Send invoice"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Box>
        <OwedFeesDetails />
      </div>
    );
  };

  const OwedFeesDetails = () => {
    if (feesDetails !== null) {
      return (
        <>
          <Dialog onClose={handleClose} open={showDialog} maxWidth={"lg"}>
            <DialogTitle
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h5">Owed Fees details</Typography>
              <Button
                startIcon={<Close />}
                style={{ marginTop: 0 }}
                onClick={() => {
                  setShowDialog(false);
                  setFeesDetails(null);
                  if (owedFeesModified) {
                    loadOwedFeesFromApi();
                    setOwedFeesModified(false);
                  }
                }}
              >
                Close
              </Button>
            </DialogTitle>

            <Card sx={{ padding: 1, overflow: "auto" }}>
              <LeadInfoBuilder
                leadData={feesDetails.collection_agent_leads}
                onRemove={requestRemoveLead}
              />
              <SmsInfoBuilder {...feesDetails.collection_agent_smss} />
            </Card>
          </Dialog>
          <RemoveFeeConfirmation />
        </>
      );
    }

    return <></>;
  };

  const RemoveFeeConfirmation = () => {
    const lead = feeToRemove?.lead;

    return (
      <Dialog open={feeToRemove !== null} onClose={cancelRemoveLead}>
        <DialogTitle>Remove this fee?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the fee for{" "}
            <strong>{lead?.customer_name || "this lead"}</strong>
            {lead?.booking_reference && ` (${lead.booking_reference})`}
            {lead?.total_to_pay !== undefined &&
              `, worth $${parseFloat(lead.total_to_pay).toFixed(2)}`}{" "}
            from this invoice. It cannot be added back from this screen.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemoveLead} disabled={removingFee}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={confirmRemoveLead}
            disabled={removingFee}
          >
            {removingFee ? "Removing..." : "Remove fee"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const LeadInfoBuilder = ({ leadData, onRemove }) => {
    if (!leadData || Object.keys(leadData).length == 0) {
      return <></>;
    }

    return (
      <Card style={{ flex: "1 1 auto" }}>
        <Table sx={{ padding: 2 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold" }}>Reference</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Added</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>
                Customer name
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}>
                Customer postcode
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}>
                Car make / model
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}>
                Car registration
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}>
                Commission value
              </TableCell>
              <TableCell style={{ fontWeight: "bold" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(leadData).map((key) => (
              <TableRow key={key}>
                <TableCell>{leadData[key].booking_reference}</TableCell>
                <TableCell>{leadData[key].lead_created}</TableCell>
                <TableCell>{leadData[key].customer_name}</TableCell>
                <TableCell>{leadData[key].user_postcode}</TableCell>
                <TableCell>
                  {leadData[key].car_make} / {leadData[key].car_model}
                </TableCell>
                <TableCell>{leadData[key].car_registration}</TableCell>
                <TableCell>
                  ${parseFloat(leadData[key].total_to_pay).toFixed(2)}
                </TableCell>
                <TableCell>
                  <Button onClick={() => onRemove(key, leadData[key])}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  LeadInfoBuilder.propTypes = {
    leadData: PropTypes.objectOf(
      PropTypes.shape({
        booking_reference: PropTypes.string,
        lead_created: PropTypes.string,
        customer_name: PropTypes.string,
        user_postcode: PropTypes.string,
        car_make: PropTypes.string,
        car_model: PropTypes.string,
        car_registration: PropTypes.string,
        total_to_pay: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        collector_fee_id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      })
    ),
    onRemove: PropTypes.func.isRequired,
  };

  const SmsInfoBuilder = (smsData) => {
    if (Object.keys(smsData).length == 0) {
      return <></>;
    }

    return (
      <Card style={{ flex: "1 1 auto" }}>
        <Table sx={{ padding: 2 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold" }}>Sms ID</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Lead ID</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Date sent</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Invoice ID</TableCell>
              <TableCell style={{ fontWeight: "bold" }}>Price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(smsData).map((key) => (
              <TableRow key={key}>
                <TableCell>{smsData[key].smss_id}</TableCell>
                <TableCell>{smsData[key].request_quote_id}</TableCell>
                <TableCell>{smsData[key].timestamp}</TableCell>
                <TableCell>{smsData[key].freshbooks_invoice_id}</TableCell>
                <TableCell>
                  ${parseFloat(smsData[key].sms_price).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  const DateTimePicker = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          views={["month", "year"]}
          maxDate={
            new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
          }
          inputFormat="MM/YYYY"
          label="From"
          value={dayjs(filteringDate)}
          onChange={handleDateChange}
          renderInput={(params) => <TextField {...params} />}
        />
      </LocalizationProvider>
    );
  };

  return (
    <CardContent>
      <Typography typography="h4" component="h1" mb={2}>
        Owed Fees
      </Typography>
      <Typography typography="h5" component="h1" mb={2}>
        Please select a month
      </Typography>
      <Box
        sx={{
          display: "flex",
          marginTop: "1rem",
          marginBottom: "1rem",
          gap: "1rem",
        }}
      >
        <Box>
          <DateTimePicker />
        </Box>
        {owedFees != null && owedFees.result !== "error" && (
          <Box>
            <Button onClick={handleExportClick}>Export</Button>
          </Box>
        )}
      </Box>
      {owedFees != null ? <OwedFeesList /> : <p>Loading...</p>}
    </CardContent>
  );
}

export default ListOwedFees;
